/*
 * FC Pre-order — PDP live refresh
 *
 * The PDP buy-buttons are server-rendered with cart-aware pre-order logic
 * (snippets/buy-buttons.liquid): once the customer has reserved all available
 * stock of a variant in their cart, the next unit is a pre-order. After an
 * add-to-cart the third-party (Opus) cart drawer updates, but Dawn does NOT
 * re-render the product form — so the banner/button would stay "In stock" until
 * a manual refresh.
 *
 * This listens for cart AND variant changes, re-fetches (or reuses) the product
 * section so the server recomputes the cart-aware state, and swaps the #BuyButtons
 * block. No pre-order logic or markup is duplicated in JS — the server stays the
 * single source of truth.
 *
 * Variant change: Dawn fetches the section for the new variant and publishes
 * PUB_SUB_EVENTS.variantChange with that `html`. The theme's own BuyButtons swap
 * doesn't fire reliably across themes (e.g. the live theme), so we swap our block
 * from the event's html directly — idempotent if the theme already swapped it.
 *
 * Every swap preserves app-injected form inputs (subscription `selling_plan`,
 * app-added `properties[...]`): they are client-side state the server render can
 * never contain, and dropping them turned subscriptions into one-time purchases.
 */
(function () {
  function getProductInfo() {
    return document.querySelector('product-info');
  }

  // App blocks (subscription widget, personalisation apps) inject their state as
  // hidden inputs INTO the product form — e.g. the subscription app's
  // `selling_plan` — and that form lives inside #BuyButtons. The server-rendered
  // swap HTML can never contain that client-side state, so a plain innerHTML
  // swap silently drops the selected plan and the next add-to-cart becomes a
  // one-time purchase. Detach app-owned nodes BEFORE the compare/swap and
  // re-attach the SAME nodes after it: node identity keeps the app's element
  // references and listeners alive. App-owned = a `selling_plan` /
  // `properties[...]` input whose name is absent from the fresh server markup;
  // theme-rendered inputs (variant id, gift-card recipient properties) are
  // never touched.
  function detachAppFormInputs(container, freshBlock) {
    var form = container.querySelector('form[action*="/cart/add"]');
    if (!form) return [];
    var freshNames = {};
    if (freshBlock) {
      freshBlock.querySelectorAll('[name]').forEach(function (el) {
        freshNames[el.getAttribute('name')] = true;
      });
    }
    var nodes = [];
    form
      .querySelectorAll(
        'input[name="selling_plan"], select[name="selling_plan"], input[name^="properties["], select[name^="properties["], textarea[name^="properties["]'
      )
      .forEach(function (el) {
        if (freshNames[el.name]) return;
        nodes.push(el);
        el.remove();
      });
    return nodes;
  }

  function restoreAppFormInputs(container, nodes) {
    if (!nodes.length) return;
    var form = container.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    nodes.forEach(function (el) {
      // Skip if something (the app itself) already re-created this field.
      for (var i = 0; i < form.elements.length; i++) {
        if (form.elements[i].name === el.name) return;
      }
      form.appendChild(el);
    });
  }

  // The banner message is SERVER-rendered into .hengam-preorder-description--custom
  // by buy-buttons.liquid via the FC message chain (variant custom.preorder_message
  // > product custom.preorder_message > translated preorder_message_default), so no
  // text is injected here anymore. This only hides the legacy Hengam (NM app)
  // description element if the app embed still renders one — re-run after every
  // BuyButtons swap in case the embed re-mounts.
  function fillPreorderDescription() {
    var originalDescEl = document.querySelector('.hengam-preorder-description');
    if (originalDescEl) originalDescEl.style.display = 'none';
  }

  function currentVariantId(productInfo) {
    var input = productInfo.querySelector('.product-variant-id');
    if (input && input.value) return input.value;
    try {
      return new URL(window.location.href).searchParams.get('variant');
    } catch (e) {
      return null;
    }
  }

  var inFlight = false;

  async function refreshBuyButtons() {
    var productInfo = getProductInfo();
    if (!productInfo || inFlight) return;

    var sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    var baseUrl = productInfo.dataset.url;
    if (!sectionId || !baseUrl) return;

    var variantId = currentVariantId(productInfo);
    var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    var url =
      baseUrl + sep +
      (variantId ? 'variant=' + encodeURIComponent(variantId) + '&' : '') +
      'section_id=' + encodeURIComponent(sectionId);

    inFlight = true;
    try {
      var res = await fetch(url);
      if (!res.ok) return;
      var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      var fresh = doc.getElementById('BuyButtons-' + sectionId);
      var current =
        productInfo.querySelector('#BuyButtons-' + productInfo.dataset.section) ||
        document.getElementById('BuyButtons-' + productInfo.dataset.section);
      if (fresh && current) {
        var preserved = detachAppFormInputs(current, fresh);
        if (fresh.innerHTML !== current.innerHTML) {
          current.innerHTML = fresh.innerHTML;
        }
        restoreAppFormInputs(current, preserved);
      }
      // Re-hide the legacy Hengam element after the swap (or after a no-op
      // match, in case Dawn swapped the block before this ran).
      fillPreorderDescription();
    } catch (e) {
      /* no-op */
    } finally {
      inFlight = false;
    }
  }

  // Swap #BuyButtons from an already-fetched document (the variantChange payload),
  // so a variant switch updates the FC banner/button with no extra request.
  // Returns false when the block isn't present in `html` (caller falls back to a
  // re-fetch). A no-op when the contents already match (theme swapped it first).
  function swapBuyButtonsFromHtml(html, sourceSectionId) {
    var productInfo = getProductInfo();
    if (!productInfo || !html || typeof html.getElementById !== 'function') return false;
    var fresh = html.getElementById('BuyButtons-' + sourceSectionId);
    var destSection = productInfo.dataset.section;
    var current =
      productInfo.querySelector('#BuyButtons-' + destSection) ||
      document.getElementById('BuyButtons-' + destSection);
    if (!fresh || !current) return false;
    var preserved = detachAppFormInputs(current, fresh);
    if (fresh.innerHTML !== current.innerHTML) current.innerHTML = fresh.innerHTML;
    restoreAppFormInputs(current, preserved);
    // Run after the swap, and also when contents already matched (Dawn swapped
    // first): the legacy app element may have re-mounted either way.
    fillPreorderDescription();
    return true;
  }

  var debounce;
  function schedule() {
    clearTimeout(debounce);
    // Small delay so the server cart is committed before we re-fetch.
    debounce = setTimeout(refreshBuyButtons, 200);
  }

  // Third-party (Opus / ego-cart-upsell) cart app events.
  window.addEventListener('OpusCartChange', schedule);
  window.addEventListener('OpusOpenCartState', schedule);

  // Native Dawn cart + variant flows.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // Add-to-cart through the theme form (re-fetch for cart-aware state).
    if (PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    // Variant switch: reuse the section html Dawn already fetched; fall back to a
    // re-fetch if the payload doesn't carry the BuyButtons block.
    if (PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var d = event && event.data;
        if (!(d && swapBuyButtonsFromHtml(d.html, d.sectionId))) {
          schedule();
        }
      });
    }
  }

  // Initial load: hide the legacy app element for a server-rendered pre-order
  // state (this script is deferred, so the DOM is parsed by the time it runs).
  fillPreorderDescription();
})();
